import type { GuardianesCharacterId } from "../components/GuardianesCharacter";
import type { GuardianesMode } from "./gameRules";

export const GUARDIANES_STORAGE_KEY = "protea.guardianes.v2";
export const GUARDIANES_LEGACY_STORAGE_KEY = "protea.guardianes.v1";
export const GUARDIANES_DISCOVERED_EVENT = "protea:guardianes-discovered";

export interface GuardianesProgress {
  schemaVersion: 2;
  discovered: boolean;
  tutorialViewed: boolean;
  selectedCharacter: GuardianesCharacterId;
  mode: GuardianesMode;
  controlsInverted: boolean;
  reducedEffects: boolean;
  challengeBestScore: number | null;
}

const DEFAULT_PROGRESS: GuardianesProgress = {
  schemaVersion: 2,
  discovered: false,
  tutorialViewed: false,
  selectedCharacter: "jellyfish",
  mode: "challenge",
  controlsInverted: false,
  reducedEffects: false,
  challengeBestScore: null,
};

let memoryProgress: GuardianesProgress = { ...DEFAULT_PROGRESS };

function getStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function validCharacter(value: unknown): value is GuardianesCharacterId {
  return value === "jellyfish" || value === "starfish" || value === "fish" || value === "turtle";
}

function validMode(value: unknown): value is GuardianesMode {
  return value === "challenge" || value === "quiet";
}

function normalizeProgress(value: Partial<GuardianesProgress>, discovered = false): GuardianesProgress {
  return {
    schemaVersion: 2,
    discovered: typeof value.discovered === "boolean" ? value.discovered : discovered,
    tutorialViewed: value.tutorialViewed === true,
    selectedCharacter: validCharacter(value.selectedCharacter) ? value.selectedCharacter : DEFAULT_PROGRESS.selectedCharacter,
    mode: validMode(value.mode) ? value.mode : DEFAULT_PROGRESS.mode,
    controlsInverted: value.controlsInverted === true,
    reducedEffects: value.reducedEffects === true,
    challengeBestScore: typeof value.challengeBestScore === "number" && Number.isFinite(value.challengeBestScore)
      ? Math.max(0, Math.round(value.challengeBestScore))
      : null,
  };
}

function readRaw(storage: Storage | null): GuardianesProgress {
  let encoded: string | null = null;
  try {
    encoded = storage?.getItem(GUARDIANES_STORAGE_KEY) ?? null;
    if (!encoded) {
      const legacy = storage?.getItem(GUARDIANES_LEGACY_STORAGE_KEY);
      if (!legacy) return storage ? { ...DEFAULT_PROGRESS } : { ...memoryProgress };
      const parsedLegacy = JSON.parse(legacy) as Partial<GuardianesProgress>;
      return normalizeProgress(parsedLegacy, parsedLegacy.discovered === true);
    }
  } catch {
    return { ...memoryProgress };
  }
  try {
    const parsed = JSON.parse(encoded) as Partial<GuardianesProgress>;
    if (parsed.schemaVersion !== 2 || typeof parsed.discovered !== "boolean") return { ...DEFAULT_PROGRESS };
    return normalizeProgress(parsed);
  } catch {
    return { ...DEFAULT_PROGRESS };
  }
}

export function readGuardianesProgress(storage: Storage | null = getStorage()): GuardianesProgress {
  const progress = readRaw(storage);
  memoryProgress = progress;
  return { ...progress };
}

export function writeGuardianesProgress(
  progress: GuardianesProgress | Partial<GuardianesProgress>,
  storage: Storage | null = getStorage(),
): boolean {
  const normalized = normalizeProgress(progress, progress.discovered === true);
  memoryProgress = normalized;
  try {
    storage?.setItem(GUARDIANES_STORAGE_KEY, JSON.stringify(normalized));
    return Boolean(storage);
  } catch {
    return false;
  }
}

export function updateGuardianesProgress(
  patch: Partial<GuardianesProgress>,
  storage: Storage | null = getStorage(),
): GuardianesProgress {
  const next = writeGuardianesProgress({ ...readGuardianesProgress(storage), ...patch }, storage);
  void next;
  return readGuardianesProgress(storage);
}

export function markGuardianesDiscovered(): GuardianesProgress {
  const progress = updateGuardianesProgress({ discovered: true });
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(GUARDIANES_DISCOVERED_EVENT));
  return progress;
}

export function saveChallengeBestScore(score: number, storage: Storage | null = getStorage()): GuardianesProgress {
  const progress = readGuardianesProgress(storage);
  if (progress.challengeBestScore !== null && progress.challengeBestScore >= score) return progress;
  return updateGuardianesProgress({ challengeBestScore: score }, storage);
}

export function hasGuardianesBeenDiscovered(): boolean {
  return readGuardianesProgress().discovered;
}
